import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, Trophy, Newspaper, CalendarDays, Users2, Menu, X, LogOut, Shield, MessageSquare, ListTree } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AdSlot } from "./AdSlot";
import client from "../lib/api";

const NAV = [
  { to: "/table", label: "Table" },
  { to: "/fixtures", label: "Fixtures" },
  { to: "/clubs", label: "Clubs" },
  { to: "/news", label: "News" },
  { to: "/fantasy", label: "Fantasy" },
  { to: "/forum", label: "Forum" },
];

const BOTTOM = [
  { to: "/", label: "Home", icon: Home },
  { to: "/fixtures", label: "Matches", icon: CalendarDays },
  { to: "/fantasy", label: "Fantasy", icon: Trophy },
  { to: "/news", label: "News", icon: Newspaper },
];

function ReservedStrip({ advertiser }) {
  return (
    <div className="bg-green text-cream" data-testid="reserved-strip">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-center gap-3 text-center">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_dynamos-portal/artifacts/avz7lzs1_327278115_1611098266054403_2476304939083726472_n.png" alt="Castle Lager Premier Soccer League crest" className="h-9 w-9 object-contain" />
        <span className="text-gold font-head font-bold text-sm md:text-base uppercase tracking-wide">
          {advertiser || "Castle Lager Premier Soccer League"}
        </span>
        <span className="hidden md:inline text-cream/70 text-xs">· Official League Partner · More than just a game</span>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [reserved, setReserved] = useState("Castle Lager Premier Soccer League");
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isFantasy = /^\/fantasy(\/|$)/.test(pathname);
  const noAds = /^\/(updates|timeline|articles|fantasy)(\/|$)/.test(pathname);

  useEffect(() => {
    const SRC = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6585219991395423";
    if (isFantasy) {
      // Fantasy is ad-free: strip any Auto Ads unit a previous page injected.
      document.querySelectorAll("ins.adsbygoogle").forEach((el) => el.remove());
      return;
    }
    if (!document.querySelector('script[data-adsense="1"]')) {
      const s = document.createElement("script");
      s.async = true;
      s.src = SRC;
      s.crossOrigin = "anonymous";
      s.setAttribute("data-adsense", "1");
      document.head.appendChild(s);
    }
  }, [isFantasy]);

  useEffect(() => {
    client.get("/ads").then(({ data }) => {
      const r = data.find((s) => s.slot_id === "league.reserved_strip");
      if (r?.advertiser) setReserved(r.advertiser);
    }).catch(() => {});
  }, []);

  const staff = user && user.role && user.role !== "fan";

  return (
    <div className="App min-h-screen flex flex-col">
      {/* Top header */}
      <header className="sticky top-0 z-40 bg-green text-cream shadow-lg">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="https://customer-assets-v7afamib.emergentagent.net/job_dynamos-portal/artifacts/avz7lzs1_327278115_1611098266054403_2476304939083726472_n.png" alt="Castle Lager Premier Soccer League shield" className="h-12 object-contain drop-shadow" />
            <div className="leading-none">
              <div className="font-head font-black text-base md:text-lg tracking-tight text-cream">Castle Lager <span className="text-gold">PSL</span></div>
              <div className="text-[10px] text-cream/80 uppercase tracking-widest">premierleaguezw.com · 2026</div>
            </div>
            <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_3b63af21-5de8-467f-8c62-12930594aa75/artifacts/jj8pj4f7_PSL.png" alt="PSL — More than just a game" className="hidden md:block h-9 object-contain bg-white rounded-sm p-1 ml-1" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `px-3 py-2 text-sm font-semibold rounded-sm transition-colors hover:text-gold ${isActive ? "text-gold" : "text-cream"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {staff && (
              <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-gold text-ink rounded-sm text-sm font-bold hover:bg-gold/80 transition-colors">
                <Shield size={15} /> Admin
              </Link>
            )}
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/account" data-testid="nav-account" className="text-sm font-semibold hover:text-gold">{user.username}</Link>
                <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="text-cream/70 hover:text-live"><LogOut size={18} /></button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden sm:block px-3 py-1.5 border border-gold text-gold rounded-sm text-sm font-bold hover:bg-gold hover:text-ink transition-colors">Sign in</Link>
            )}
            <button className="lg:hidden text-cream" onClick={() => setOpen(!open)} data-testid="menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-green-900 border-t border-cream/10 px-4 py-3 grid grid-cols-2 gap-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-2 text-sm font-semibold rounded-sm ${isActive ? "text-gold" : "text-cream"}`}>{n.label}</NavLink>
            ))}
            <Link to="/fantasy/chat" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-cream">Fantasy Chat</Link>
            <Link to="/advertise" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-cream">Advertise</Link>
            {staff && <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-bold text-gold">Admin</Link>}
            {user ? <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="px-3 py-2 text-sm font-semibold text-live text-left">Log out</button>
                  : <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-bold text-gold">Sign in</Link>}
          </div>
        )}
      </header>

      {/* Masthead ad */}
      {!noAds && (
        <div className="max-w-6xl mx-auto w-full px-4 pt-4">
          <AdSlot id="masthead.leaderboard" height={80} />
        </div>
      )}

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6 pb-24 lg:pb-6 animate-fade-up">
        <Outlet />
      </main>

      {/* Platform footer board */}
      {!noAds && (
        <div className="max-w-6xl mx-auto w-full px-4 pb-4">
          <AdSlot id="community.footer_board" height={70} />
        </div>
      )}

      <footer className="bg-ink text-cream/70">
        <div className="max-w-6xl mx-auto px-4 py-8 grid md:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="font-head font-black text-cream text-lg">PSL 2026</div>
            <p className="mt-2 text-cream/50">The official free digital home of the Castle Lager Premier Soccer League. Zimbabwe-first.</p>
          </div>
          <div>
            <div className="font-bold text-gold uppercase text-xs tracking-widest mb-2">League</div>
            <div className="flex flex-col gap-1">
              <Link to="/table" className="hover:text-gold">Table</Link>
              <Link to="/fixtures" className="hover:text-gold">Fixtures & Results</Link>
              <Link to="/clubs" className="hover:text-gold">Clubs</Link>
            </div>
          </div>
          <div>
            <div className="font-bold text-gold uppercase text-xs tracking-widest mb-2">Play & Read</div>
            <div className="flex flex-col gap-1">
              <Link to="/fantasy" className="hover:text-gold">Fantasy</Link>
              <Link to="/news" className="hover:text-gold">News</Link>
              <Link to="/forum" className="hover:text-gold">Forum</Link>
            </div>
          </div>
          <div>
            <div className="font-bold text-gold uppercase text-xs tracking-widest mb-2">Business</div>
            <div className="flex flex-col gap-1">
              <Link to="/advertise" className="hover:text-gold">Advertise</Link>
              <Link to="/account" className="hover:text-gold">My account</Link>
              <Link to="/feedback" className="hover:text-gold">Send feedback</Link>
              <Link to="/install" className="hover:text-gold">Install app</Link>
            </div>
          </div>
          <div>
            <div className="font-bold text-gold uppercase text-xs tracking-widest mb-2">Legal & Contact</div>
            <div className="flex flex-col gap-1">
              <Link to="/about" className="hover:text-gold">About</Link>
              <Link to="/contact" className="hover:text-gold">Contact</Link>
              <Link to="/privacy-policy" className="hover:text-gold">Privacy Policy</Link>
              <Link to="/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-gold">Terms</Link>
              <Link to="/disclaimer" className="hover:text-gold">Disclaimer</Link>
              <Link to="/childrens-policy" className="hover:text-gold">Children's Policy</Link>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-6 text-xs text-cream/40 border-t border-cream/10 pt-4">
          <p><b className="text-cream/60">16+ only.</b> Independent fan site — not affiliated with the Zimbabwe PSL, ZIFA or any club. Operated by Patrons Nest Ltd (UK), company no. 15011254, Lincoln, England.</p>
        </div>
      </footer>
      <ReservedStrip advertiser={reserved} />

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-green border-t border-gold/30 grid grid-cols-4" data-testid="bottom-nav">
        {BOTTOM.map((b) => (
          <NavLink key={b.to} to={b.to} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[11px] font-semibold ${isActive ? "text-gold" : "text-cream/80"}`}>
            <b.icon size={20} /> {b.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
